import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AmountInputBox } from 'pages/Landing/components/AmountInputBox';
import styled from '@emotion/styled';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { getErc20Contract } from 'utils/contractHelpers';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import {
	getDepositContract,
	getNativeDepositContract,
} from 'utils/depositContractHelpers';
import { toast } from 'react-toastify';
import {
	getClaimList,
	getUserDeposit,
	userDeposit,
	UserDepositParams,
	UserWithdrawDeposit,
} from 'apis';
import { useEthersProvider } from 'hooks/useEtherProvider';
import { useEthersSigner } from 'hooks/useEtherSigner';
import { useAccount, useBalance } from 'wagmi';
import { authActions } from 'store/auth';
import { getErrorMessage } from 'data/errors';
import { useLocation } from 'react-router-dom';
import { ethers } from 'ethers';
import { isPassed } from 'utils/lockDuration';
import { DEPOSIT_LOCK_TIME } from 'data/constant';

//const twitterSocial = SOCIAL.find((x) => x.name.toLowerCase() === 'twitter');

const Column = styled('div')`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const Row = styled('div')`
	display: flex;
	flex-direction: row;
`;

const SpaceBetween = styled(Column)(() => ({
	height: '100%',
	justifyContent: 'space-between',
	padding: 20,
	borderRadius: 20,
	backgroundColor: '#fff',
	border: `1px solid #e2e2e2`,
}));

const TextButton = styled(Typography)`
	:hover {
		cursor: pointer;
	}
`;

const StyledButton = styled(Button)<{ disabled?: boolean }>`
	background-color: ${({ disabled }) => (disabled ? '#dadada' : '#2dbcb8')};
	align-self: 'center';
	border-radius: 40px;

	:hover {
		cursor: ${({ disabled }) => (disabled ? 'no-drop' : 'pointer')};
		background-color: #36454f;
	}
`;

/*
const ButtonText = styled(Typography)`
	font-weight: 400;
	cursor: pointer;
	color: #275ff1;
`;
*/

const PrimaryButton = styled(Button)(() => ({
	backgroundColor: '#29292c',
	width: '100%',
	whiteSpace: 'nowrap',
	color: '#FFFFFF',
	padding: '12px 10px',
	fontFamily: 'Inter, sans-serif',
	borderRadius: 15,
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',

	'&:hover': {
		backgroundColor: '#4d4d52',
		boxShadow: 'none',
	},

	'&:disabled': {
		backgroundColor: '#818089',
		boxShadow: 'none',
	},
}));

/*const StyledBox = styled('div')`
	display: flex;
	flex-direction: row;
	align-items: start;
	width: 100%;
	padding: 22px 16px;
	background: #fff;
	border-radius: 5px;
	border: 1px solid #b7b8bf;
`;*/

/*const SecondaryButton = styled(Button)(() => ({
	backgroundColor: '#8A8C92',
	width: '100%',
	whiteSpace: 'nowrap',
	color: '#FFFFFF',
	padding: '12px 10px',
	fontFamily: 'AtAmiga',
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',
	clipPath:
		'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
	'&:hover': {
		backgroundColor: '#707277',
		boxShadow: 'none',
	},
}));*/

const Segmented = styled('div')`
	display: flex;
	width: 100%;
	background: #f2f2f5;
	border-radius: 14px;
	padding: 4px;
`;

const Segment = styled(Button)<{ active?: boolean }>`
	flex: 1;
	border-radius: 12px !important;
	text-transform: none !important;
	font-weight: 600 !important;
	background: ${({ active }) =>
		active ? '#ffffff' : 'transparent'} !important;
	color: #1f2937 !important;
	box-shadow: none !important;
	font-family: Inter, sans-serif;

	:disabled {
		color: #a7a7a9 !important;
		cursor: not-allowed;
	}
`;

const Tip = styled('div')`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 14px 16px;
	background: #f7fbff;
	border: 1px solid #cfe8ff;
	border-radius: 10px;
`;

const AmountHeader = styled('div')`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
`;

const QuickRow = styled('div')`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const EarningsCard = styled('div')`
	display: flex;
	flex-direction: column;
	gap: 14px;
	background: #f4f5f7;
	border-radius: 10px;
	padding: 18px 16px;
	//border: 1px solid #d7d8dd;
`;

export const DepositPanel: React.ComponentType<{
	onSuccess?(): void;
	userContractBalance?: string;
}> = ({ onSuccess, userContractBalance }) => {
	const [userDeposits, setUserDeposits] = useState<{
		lastDeposit: Required<UserWithdrawDeposit>[];
	}>({ lastDeposit: [] });
	const [claims, setClaims] = useState<UserWithdrawDeposit[]>([]);
	const [loading, setLoading] = useState(false);
	const [allowance, setAllowance] = useState('0');
	const [balance, setBalance] = useState('0');
	//const [contractBal, setContractBal] = useState('0');
	const [amount, setAmount] = useState('');
	const [tab, setTab] = useState<'deposit' | 'withdraw'>('deposit');
	const { address: account, isConnected } = useAccount();
	const dispatch = useDispatch();
	const { data } = useBalance({ address: account });
	const nativeBalance = data?.formatted || '0';
	const location = useLocation();
	const selectedVault = useSelector((state: RootState) => state.vault);
	const selectedAsset = selectedVault.token.find(
		(d) => d.name === selectedVault.selectedToken,
	);
	const selectedExchange = selectedVault.exchanger.find(
		(d) => d.value === selectedVault.exchange,
	);
	const queryParams = new URLSearchParams(location.search);
	const devMode = queryParams.get('devmode');
	const minAllowed = devMode ? 0.1 : selectedAsset?.minAllowed || 0;

	const provider: any = useEthersProvider();
	const signer: any = useEthersSigner();
	const payload = useMemo(() => {
		return {
			contract: selectedExchange?.depositContractAddress || '',
			address: account || '',
		};
	}, [account, selectedExchange?.depositContractAddress]);

	const isDisabled =
		parseFloat(amount || '0') >= (selectedAsset?.maxAllowed || 10000) ||
		parseFloat(amount || '0') < minAllowed;
	const disabledMessage =
		Number(amount) < minAllowed
			? `Min ${minAllowed}${selectedVault.currency}`
			: `Max ${selectedAsset?.maxAllowed || 10000}`;

	const formattedBalance = Number(balance).toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	const formattedContractBal = Number(userContractBalance).toLocaleString(
		undefined,
		{
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		},
	);

	const getUserDeposits = useCallback(async (value: UserDepositParams) => {
		try {
			const res = await getUserDeposit(value);
			setUserDeposits(res);
		} catch (e) {
			//
		}
	}, []);

	const fetchAllowance = useCallback(async () => {
		try {
			if (account && selectedAsset && selectedExchange) {
				const assetContract = getErc20Contract(
					selectedAsset.address,
					provider.getSigner(account).connectUnchecked(),
				);
				const assetAllowance = await assetContract.allowance(
					account,
					selectedExchange.depositContractAddress,
				);
				setAllowance(
					formatUnits(assetAllowance, selectedAsset.decimalValue) ||
						'0',
				);
			} else {
				setAllowance('0');
			}
		} catch {
			//
		}
	}, [account, provider, selectedAsset, selectedExchange]);

	const fetchBalance = useCallback(async () => {
		try {
			if (account && selectedAsset && signer) {
				const assetContract = getErc20Contract(
					selectedAsset.address,
					signer.provider.getSigner(account).connectUnchecked(),
				);
				const assetBalance = await assetContract.balanceOf(account);
				setBalance(
					formatUnits(assetBalance, selectedAsset.decimalValue),
				);
			} else {
				setBalance('0');
			}
		} catch {
			//
		}
	}, [account, signer, selectedAsset]);

	const fetchClaims = useCallback(async () => {
		try {
			if (account) {
				const res = await getClaimList(
					selectedExchange?.depositContractAddress || '',
					account,
				);

				setClaims(res);
			}
		} catch (e) {
			//
		}
	}, [account, selectedExchange?.depositContractAddress]);

	const onNativeDeposit = async () => {
		try {
			setLoading(true);

			if (!account || !selectedExchange || !signer) return;

			const lockTime =
				userDeposits.lastDeposit.length > 0 &&
				isPassed(
					userDeposits.lastDeposit[
						userDeposits.lastDeposit.length - 1
					].createdAt,
					DEPOSIT_LOCK_TIME,
				);

			if (lockTime && lockTime?.isLocked) {
				toast(
					`You can deposit again after ${lockTime?.newTime.format(
						'DD-MMM, hh:mm A',
					)}`,
					{
						type: 'error',
					},
				);
				return;
			}

			if (claims.filter((d) => d.eventType === 'claim').length > 0) {
				toast(
					'Your deposit claim request is in the progress. You can deposit after withdrawal.',
					{
						type: 'error',
					},
				);
				return;
			}

			if (nativeBalance < amount) {
				toast('Wallet does not have sufficient funds to deposit', {
					type: 'error',
				});
				return;
			}

			const depositValue = ethers.utils.parseEther(amount);

			const contract = getNativeDepositContract(
				selectedExchange?.depositContractAddress || '',
				signer.provider.getSigner(account).connectUnchecked(),
			);

			const dpt = await contract.depositNative(account, {
				value: depositValue,
			});
			await dpt.wait();

			await userDeposit({
				contract: selectedExchange?.depositContractAddress || '',
				address: account || '',
				amount,
				token: selectedVault.selectedToken,
				eventType: 'deposit',
			});

			toast('Successfully staked into vault contract', {
				type: 'success',
			});
			onSuccess?.();
			setAmount('0');
			await getUserDeposits(payload);
		} catch (e: any) {
			onSuccess?.();
			toast(
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '0.75rem',
					}}
				>
					<div>
						{getErrorMessage(e?.error?.code || e?.code, e) ||
							'Something went wrong'}
					</div>

					{getErrorMessage(e?.error?.code || e?.code, e).includes(
						'Something went wrong',
					) && (
						<StyledButton
							onClick={() => {
								window.open(
									'https://t.me/+96H-v4meO1szMmJk',
									'_blank',
								);
							}}
							style={{ height: 'min-content', padding: 5 }}
						>
							<Typography
								variant="caption"
								fontWeight="bold"
								py="6px"
								color="#fff"
								style={{ lineHeight: '120%' }}
							>
								Support
							</Typography>
						</StyledButton>
					)}
				</div>,
				{
					type: 'error',
				},
			);
		} finally {
			setLoading(false);
		}
	};

	const onDeposit = async () => {
		try {
			setLoading(true);

			if (!account || !selectedAsset || !selectedExchange || !signer)
				return;

			/*const res = await fetchIpInformation();
			if (res) {
				toast(
					'We regret to inform you that our services are currently\n' +
						'unavailable in certain regions due to regulatory and\n' +
						'compliance constraints. The restricted regions include:' +
						RESTRICTED_REGION.toString().replace(',', ', '),
					{
						type: 'error',
					},
				);
				return;
			}*/

			if (claims.filter((d) => d.eventType === 'claim').length > 0) {
				toast(
					'Your deposit claim request is in the progress. You can deposit after withdrawal.',
					{
						type: 'error',
					},
				);
				return;
			}
			const lockTime =
				userDeposits.lastDeposit.length > 0 &&
				isPassed(
					userDeposits.lastDeposit[
						userDeposits.lastDeposit.length - 1
					].createdAt,
					DEPOSIT_LOCK_TIME,
				);

			if (lockTime && lockTime?.isLocked) {
				toast(
					`You can deposit again after ${lockTime?.newTime.format(
						'DD-MMM, hh:mm A',
					)}`,
					{
						type: 'error',
					},
				);
				return;
			}

			const assetContract = getErc20Contract(
				selectedAsset?.address || '',
				signer.provider.getSigner(account).connectUnchecked(),
			);

			const assetBalance = await assetContract.balanceOf(account);
			const requiredAmount = parseUnits(
				amount,
				selectedAsset?.decimalValue || '',
			);

			if (assetBalance.lt(requiredAmount)) {
				toast('Wallet does not have sufficient funds to deposit.', {
					type: 'error',
				});
				return;
			}

			const contract = getDepositContract(
				selectedExchange?.depositContractAddress || '',
				signer.provider.getSigner(account).connectUnchecked(),
			);

			if (parseFloat(allowance) < parseFloat(amount)) {
				const tx = await assetContract.approve(
					selectedExchange?.depositContractAddress,
					requiredAmount,
				);
				await assetContract.provider.waitForTransaction(tx.hash);

				const updatedAllowance = await assetContract.allowance(
					account,
					selectedExchange?.depositContractAddress,
				);

				if (updatedAllowance.lt(requiredAmount)) {
					toast('Please allow USDC spending', { type: 'error' });
					return;
				}
			}

			const dpt = await contract.deposit(requiredAmount, account);
			await dpt.wait();

			await userDeposit({
				contract: selectedExchange?.depositContractAddress || '',
				address: account || '',
				amount,
				token: 'USDC',
				eventType: 'deposit',
			});

			toast('Successfully staked into vault contract', {
				type: 'success',
			});
			onSuccess?.();
			setAmount('0');
			await getUserDeposits(payload);
		} catch (e: any) {
			onSuccess?.();
			toast(
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '0.75rem',
					}}
				>
					<div>
						{getErrorMessage(e?.error?.code || e?.code, e) ||
							'Something went wrong'}
					</div>

					{getErrorMessage(e?.error?.code || e?.code, e).includes(
						'Something went wrong',
					) && (
						<StyledButton
							onClick={() => {
								window.open(
									'https://t.me/+96H-v4meO1szMmJk',
									'_blank',
								);
							}}
							style={{ height: 'min-content', padding: 5 }}
						>
							<Typography
								variant="caption"
								fontWeight="bold"
								py="6px"
								color="#fff"
								style={{ lineHeight: '120%' }}
							>
								Support
							</Typography>
						</StyledButton>
					)}
				</div>,
				{
					type: 'error',
				},
			);
		} finally {
			fetchBalance();
			fetchAllowance();
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchAllowance();
		fetchBalance();
		fetchClaims();
		getUserDeposits(payload);
	}, [fetchClaims, fetchAllowance, fetchBalance, getUserDeposits, payload]);

	const isFirstDeposit = (Number(userContractBalance || 0) || 0) === 0;

	return (
		<SpaceBetween>
			<Column>
				<Segmented>
					<Segment
						active={tab === 'deposit'}
						onClick={() => setTab('deposit')}
					>
						Deposit
					</Segment>
					<Segment
						active={tab === 'withdraw'}
						onClick={() => setTab('withdraw')}
						disabled
					>
						Withdraw (No deposits)
					</Segment>
				</Segmented>

				<Box height={12} />

				<Tip>
					<Typography
						variant="caption"
						fontWeight={600}
						display={'inline'}
						color={'#4763c6'}
					>
						💡 New user tip:{' '}
						<Typography
							variant="caption"
							display={'inline'}
							color={'#4763c6'}
						>
							Start with $100–500 to get familiar with the
							platform!
						</Typography>
					</Typography>
				</Tip>

				<Box height={16} />

				{/*<StyledBox>
					<Column>
						<Row style={{ alignItems: 'center' }}>
							<img
								src={Globe}
								width={24}
								height={24}
								alt={'globe'}
							/>
							<Gutter gap={0.5} />
							<Typography variant={'h6'}>
								{selectedVault.name}
							</Typography>
						</Row>
						<Box height={8} />
						<Row
							style={{
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							<Typography variant={'body2'} fontWeight={400}>
								{selectedVault.shortDescription}
							</Typography>
							<ButtonText
								variant={'body2'}
								onClick={() => {
									window.open(twitterSocial?.url, '_blank');
								}}
							>
								{`${selectedVault.name}`.toUpperCase()}
							</ButtonText>
						</Row>
					</Column>
				</StyledBox>

				<Box height={16} />*/}

				<AmountHeader>
					<Typography variant="subtitle1" fontWeight={700}>
						Amount ({selectedVault.token[0].name})
					</Typography>
				</AmountHeader>

				<Box height={8} />

				<AmountInputBox
					currency={{
						icon: selectedVault.token[0].icon,
						name: selectedVault.token[0].name,
					}}
					text={amount}
					onChange={setAmount}
					leftTxt={`${selectedVault.currency} ${formattedBalance}`}
					rightTxt={`${selectedVault.currency} ${formattedContractBal}`}
				/>

				{/*<Box height={8} />*/}

				<AmountHeader>
					<Typography
						variant="caption"
						color="#6b7280"
						fontWeight={500}
					>
						Balance: {selectedVault.currency} {formattedBalance}
					</Typography>
					<QuickRow>
						<TextButton
							variant="caption"
							onClick={() => setAmount('100')}
							fontWeight={500}
						>{`25${selectedVault.token[0].name}`}</TextButton>
						<TextButton
							variant="caption"
							onClick={() => setAmount('500')}
							fontWeight={500}
						>{`50${selectedVault.token[0].name}`}</TextButton>
						<TextButton
							variant="caption"
							onClick={() =>
								setAmount(String(Math.floor(Number(balance))))
							}
							fontWeight={500}
						>
							MAX
						</TextButton>
					</QuickRow>
				</AmountHeader>

				<Box height={20} />

				<EarningsCard>
					<Row
						style={{
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography variant="subtitle2" fontWeight={500}>
							Est. Daily Earnings
						</Typography>
						<Typography
							variant="subtitle2"
							fontWeight={500}
							color="#08a63f"
						>
							$0.00
						</Typography>
					</Row>
					<Row
						style={{
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography variant="subtitle2" fontWeight={500}>
							Est. Monthly Earnings
						</Typography>
						<Typography
							variant="subtitle2"
							fontWeight={500}
							color="#08a63f"
						>
							$0.00
						</Typography>
					</Row>
					<Row
						style={{
							justifyContent: 'space-between',
							width: '100%',
						}}
					>
						<Typography variant="subtitle2" fontWeight={500}>
							Est. Yearly Earnings
						</Typography>
						<Typography
							variant="subtitle2"
							fontWeight={500}
							color="#08a63f"
						>
							$0.00
						</Typography>
					</Row>
				</EarningsCard>

				<Box height={24} />

				<PrimaryButton
					onClick={() => {
						if (!isConnected) {
							dispatch(authActions.openDrawer(true));
						} else if (selectedExchange?.depositDisabled) {
							toast('Deposits are not available at this moment', {
								type: 'success',
								icon: false,
								style: { backgroundColor: '#fff' },
								progressStyle: { background: '#2dbcbb' },
							});
						} else if (selectedAsset?.isNative) {
							onNativeDeposit();
						} else {
							onDeposit();
						}
					}}
					disabled={
						true
						/*loading ||
						(isConnected && parseFloat(amount || '0') === 0) ||
						(isConnected && isDisabled)*/
					}
					sx={{ height: 52 }}
					fullWidth
				>
					<Typography
						variant={'button'}
						fontWeight="bold"
						py="6px"
						fontFamily={'Inter'}
						color={'#fff'}
						style={{ lineHeight: '120%' }}
					>
						{(!isConnected
							? 'Connect'
							: isDisabled
							? disabledMessage
							: loading
							? 'Please wait...'
							: isFirstDeposit
							? 'Make First Deposit'
							: 'Deposit'
						).toUpperCase()}
					</Typography>
				</PrimaryButton>
			</Column>
		</SpaceBetween>
	);
};
